import { ISupplier } from '@helpers/types/supplier';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useFormContext, useWatch } from 'react-hook-form';
import css from './index.module.scss';
import Typography from '@components/base/typography';
import {
  AutoComplete,
  DocumentRow,
  InputDatePicker,
  SelectLabel,
  TextField
} from '@components/common';
import { Button, Chip, IconNode, Modal, ToolTip } from '@components/base';
import Images from '@assets/images';
import {
  editRequestPayment,
  invoiceFilterOptions,
  invoicePayment,
  orderFilterOptions,
  requestPayment,
  supplierFilterOptions
} from '@services/finance.service';
import notify from '@helpers/toastify-helper';
import moment from 'moment';
import { requestPaymentSchema } from '@helpers/yup/payment-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PAYMENT_STATUS } from '@helpers/constants';
import { ISelect } from '@helpers/types';

interface ISupplierIdName {
  supplier_id: string;
  supplier_name: string;
}

interface IInvoiceNumber {
  invoice_number: string;
  invoice_info_id: string;
}
interface IInvoice {
  invoice_info_id: string;
  invoice_number: string;
  invoice_date: Date | null;
  total_amount_eligible_for_payment: string;
  total_amount_eligible_to_request: string;
  currency: string;
  amount: string;
  gst_amount: string;
  document: string;
  tds_amount: string;
  tcs_amount: string;
  additional_charges_or_discounts: string;
  remarks: string;
  payment_terms: string;
  gross_invoice_amount: string;
  total_amount_requested: string;
  total_amount_initiated: string;
  total_amount_paid: string;
  total_child_invoices_gross_amount: string;
  supplier_id?: string;
  supplier_name?: string;
}

interface IOrderList {
  order_id: string;
  readable_order_id: string;
}

interface IRequestPaymentState {
  isLoading: boolean;
  order: IOrderList | null;
  supplier: ISupplierIdName | null;
  orderOptions: IOrderList[];
  invoice: IInvoice | null;
  supplierOptions: ISupplier[];
  invoiceOptions: IInvoice[];
  invoiceDetails: IInvoice | null;
  showAmountDetails: boolean;
}

interface IRequestFormInvoice {
  invoice_info_id: string | null;
  invoice_number: string | null;
  invoice_date: Date | null;
  total_amount_eligible_for_payment: string | null;
  total_amount_eligible_to_request: string | null;
  currency: ISelect | null;
  amount: string | null;
  gst_amount: string | null;
  document: string | null;
  tds_amount: string | null;
  tcs_amount: string | null;
  additional_charges_or_discounts: string | null;
  remarks: string;
  payment_terms: string | null;
  net_amount: number | null;
  gross_amount: string | null;
}

interface IRequestPaymentForm {
  order: IOrderList | null;
  supplier: ISupplierIdName | null;
  invoice_number: IInvoiceNumber | null;
  invoice: IRequestFormInvoice | null;
  amount_request: string | null;
  due_date: Date | null;
  requester_remarks: string | null;
  amount_request_decimal: number | null;
}

interface IEditData {
  payment_id: string;
  invoice_info_id: string;
  invoice_number: string;
  amount_requested: string;
  due_date: Date;
  requester_remarks: string;
}

interface IRequestPayment {
  open: boolean;
  onClose: () => void;
  handleFetchPaymentData: (status?: string) => void;
  handlePaymentChipClick: (value: number) => void;
  editData?: IEditData | null;
  handleTabChange?: (_event: SyntheticEvent, newValue: number) => void;
}

const RequestPayment = (props: IRequestPayment) => {
  const { open, onClose, handleFetchPaymentData, handlePaymentChipClick, editData = null } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubTabParam = searchParams.get('activeSubTab');
  const activeTab = searchParams.get('activeTab');
  const [requestPaymentState, setRequestPaymentState] = useState<IRequestPaymentState>({
    isLoading: false,
    order: null,
    supplier: null,
    invoice: null,
    orderOptions: [],
    supplierOptions: [],
    invoiceOptions: [],
    invoiceDetails: null,
    showAmountDetails: false
  });

  const isEdit = useMemo(() => {
    return editData ? true : false;
  }, [editData]);

  const [paymentEligibleToRequest, setPaymentEligibleToRequest] = useState<number>(0);

  const {
    isLoading,
    supplierOptions,
    invoiceOptions,
    invoiceDetails,
    supplier,
    showAmountDetails,
    order,
    orderOptions
  } = requestPaymentState;

  const requestPaymentForm = useForm<IRequestPaymentForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(requestPaymentSchema),
    defaultValues: {
      order: null,
      supplier: null,
      invoice_number: null,
      invoice: {
        invoice_info_id: null,
        invoice_number: null,
        invoice_date: null,
        total_amount_eligible_for_payment: null,
        total_amount_eligible_to_request: null,
        currency: null,
        amount: null,
        gst_amount: null,
        document: null,
        tds_amount: null,
        tcs_amount: null,
        additional_charges_or_discounts: null,
        remarks: '',
        payment_terms: null,
        net_amount: null,
        gross_amount: null
      },
      amount_request: null,
      due_date: null,
      requester_remarks: null,
      amount_request_decimal: null
    }
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    getValues,
    setError,
    watch
  } = requestPaymentForm;

  const amountRequestWatch = useWatch({
    control,
    name: 'amount_request'
  });
  const invoiceWatch = watch('invoice_number');

  const fetchOrderDetails = async (event: any, params?: any) => {
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: true
    }));
    const response = await orderFilterOptions(params);
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: false
    }));
    if (response.success) {
      setRequestPaymentState((prevState: IRequestPaymentState) => ({
        ...prevState,
        orderOptions: response.data.results,
        isLoading: false
      }));
    } else if (response.error) {
      notify({
        message: response.error.message ?? 'Something went wrong, Contact Tech Team',
        severity: 'error'
      });
    }
  };

  const fetchSupplierDetails = async (event: any, params?: any) => {
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: true
    }));
    const response = await supplierFilterOptions(params);
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: false
    }));
    if (response.success) {
      setRequestPaymentState((prevState: IRequestPaymentState) => ({
        ...prevState,
        supplierOptions: response.data,
        isLoading: false
      }));
    } else if (response.error) {
      notify({
        message: response.error.message ?? 'Something went wrong, Contact Tech Team',
        severity: 'error'
      });
    }
  };

  const fetchInvoiceDetails = async (event: any, params?: any) => {
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: true
    }));
    const response = await invoiceFilterOptions(params);
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: false
    }));
    if (response.success) {
      setRequestPaymentState((prevState: IRequestPaymentState) => ({
        ...prevState,
        invoiceOptions: response.data.results
      }));
    } else if (response.error) {
      notify({
        message: response.error.message ?? 'Something went wrong, Contact Tech Team',
        severity: 'error'
      });
    }
  };

  const fetchInvoiceDetailsById = async (invoice_info_id: string) => {
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: true
    }));
    const response = await invoicePayment(invoice_info_id);
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      isLoading: false
    }));
    if (response.success) {
      const { data } = response;
      setRequestPaymentState((prevState: IRequestPaymentState) => ({
        ...prevState,
        invoiceDetails: data
      }));
      const supplier = {
        supplier_id: data.supplier,
        supplier_name: data.supplier_name
      };
      requestPaymentForm.setValue('invoice.invoice_info_id', data.invoice_info_id);
      requestPaymentForm.setValue('invoice.invoice_number', data.invoice_number);
      requestPaymentForm.setValue(
        'invoice.total_amount_eligible_for_payment',
        data.total_amount_eligible_for_payment
      );
      requestPaymentForm.setValue(
        'invoice.total_amount_eligible_to_request',
        isEdit && editData
          ? (
              parseFloat(data.total_amount_eligible_to_request || '0') +
              parseFloat(editData.amount_requested || '0')
            ).toString()
          : data.total_amount_eligible_to_request
      );
      isEdit && editData
        ? setPaymentEligibleToRequest(
            parseFloat(data.total_amount_eligible_to_request || '0') +
              parseFloat(editData.amount_requested || '0')
          )
        : setPaymentEligibleToRequest(data.total_amount_eligible_to_request);
      requestPaymentForm.setValue('invoice.invoice_date', new Date(data.invoice_date));
      requestPaymentForm.setValue('invoice.currency', {
        label: data.currency,
        value: data.currency
      });
      requestPaymentForm.setValue('invoice.amount', data.amount);
      requestPaymentForm.setValue('invoice.gst_amount', data.gst_amount);
      requestPaymentForm.setValue('invoice.gross_amount', data.gross_invoice_amount);
      requestPaymentForm.setValue('invoice.tds_amount', data.tds_amount);
      requestPaymentForm.setValue('invoice.tcs_amount', data.tcs_amount);
      requestPaymentForm.setValue(
        'invoice.additional_charges_or_discounts',
        data.additional_charges_or_discounts
      );
      requestPaymentForm.setValue('supplier', supplier as ISupplierIdName);
      let netInvoiceAmount = 0;
      netInvoiceAmount += parseFloat(data.gross_invoice_amount ?? '0');
      if (parseFloat(data.tds_amount)) {
        netInvoiceAmount -= parseFloat(data.tds_amount);
      } else if (parseFloat(data.tcs_amount)) {
        netInvoiceAmount += parseFloat(data.tcs_amount);
      }
      if (parseFloat(data.additional_charges_or_discounts)) {
        netInvoiceAmount += parseFloat(data.additional_charges_or_discounts);
      }
      requestPaymentForm.setValue('invoice.net_amount', netInvoiceAmount);
      requestPaymentForm.setValue('invoice.document', data.document);

      requestPaymentForm.setValue('invoice.remarks', data.remarks);
      requestPaymentForm.setValue('invoice.payment_terms', data.payment_terms_display);
      if (isEdit && editData) {
        const percentAmountRequestable =
          (parseFloat(editData.amount_requested ?? '0') /
            parseFloat(data?.total_amount_eligible_for_payment ?? '0')) *
          100;
        requestPaymentForm.setValue('amount_request', percentAmountRequestable.toString());
      }
    } else if (response.error) {
      notify({
        message: response.error.message ?? 'Something went wrong, Contact Tech Team',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    if (!isEdit) {
      fetchOrderDetails(null);
    }
    if (isEdit && editData) {
      fetchInvoiceDetailsById(editData.invoice_info_id);
      requestPaymentForm.setValue('amount_request_decimal', parseFloat(editData.amount_requested));
      requestPaymentForm.setValue('due_date', new Date(editData.due_date));
      requestPaymentForm.setValue('requester_remarks', editData.requester_remarks);
      requestPaymentForm.setValue('invoice_number', {
        invoice_info_id: editData.invoice_info_id,
        invoice_number: editData.invoice_number
      });
    }
  }, []);

  useEffect(() => {
    !isEdit && fetchSupplierDetails(null, { order_id: order?.order_id });
  }, [order]);

  useEffect(() => {
    !isEdit &&
      fetchInvoiceDetails(null, { supplier_id: supplier?.supplier_id, order_id: order?.order_id });
  }, [supplier]);

  const onFormSubmit = async (data: IRequestPaymentForm) => {
    if (paymentEligibleToRequest < 0) {
      setError('amount_request', {
        type: 'custom',
        message: 'Requested amount cannot be more than amount eligible to request'
      });
      return;
    }
    const requestBody = {
      currency: data?.invoice?.currency?.value,
      amount_to_pay: data?.amount_request_decimal,
      due_date: moment(data.due_date).format('YYYY-MM-DD'),
      requester_remarks: data.requester_remarks
    };
    let response;
    if (data?.invoice?.invoice_info_id) {
      response =
        isEdit && editData
          ? await editRequestPayment(editData.payment_id, requestBody)
          : await requestPayment(data.invoice.invoice_info_id, requestBody);
    } else {
      notify({
        message: 'Invoice ID is missing',
        severity: 'error'
      });
      return;
    }
    if (response.success) {
      notify({
        message: response.data.message ?? 'Payment Requested Successfully',
        severity: 'success'
      });
      onClose();
      if (activeTab && activeTab == 'invoices') {
        navigate('/finance?activeTab=payments&page=1');
      }
      if (activeSubTabParam && activeSubTabParam != PAYMENT_STATUS.REQUESTED) {
        handlePaymentChipClick(0);
      } else {
        handleFetchPaymentData(PAYMENT_STATUS.REQUESTED);
      }
    } else if (response.error) {
      notify({
        message: response.error.message ?? 'Something went wrong, Contact Tech Team',
        severity: 'error'
      });
    }
  };

  const onOrderSelection = (event: any, order: any) => {
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      order: order
    }));
    requestPaymentForm.resetField('supplier');
    requestPaymentForm.resetField('invoice_number');
    requestPaymentForm.setValue('order', order);
  };

  const onSupplierSelection = (event: any, supplier: ISupplier) => {
    setRequestPaymentState((prevState: IRequestPaymentState) => ({
      ...prevState,
      supplier: supplier
    }));
    requestPaymentForm.resetField('invoice_number');
    requestPaymentForm.setValue('supplier', supplier);
  };

  const onInvoiceSelection = (event: any, invoice: IInvoice) => {
    const supplier = { supplier_id: invoice?.supplier_id, supplier_name: invoice?.supplier_name };
    invoice?.invoice_info_id?.length > 0 && fetchInvoiceDetailsById(invoice.invoice_info_id);
    if (invoice) {
      setRequestPaymentState((prevState: IRequestPaymentState) => ({
        ...prevState,
        invoice: invoice,
        supplier: supplier as ISupplierIdName
      }));
      requestPaymentForm.setValue('invoice_number', {
        invoice_number: invoice.invoice_number ?? null,
        invoice_info_id: invoice.invoice_info_id
      });
      requestPaymentForm.setValue('supplier', supplier as ISupplierIdName);
    } else {
      requestPaymentForm.resetField('invoice_number');
      setRequestPaymentState((prevState: IRequestPaymentState) => ({
        ...prevState,
        invoice: invoice
      }));
    }
    requestPaymentForm.setValue('amount_request', null);
    requestPaymentForm.setValue('amount_request_decimal', null);
  };

  const handleCancel = () => {
    onClose();
    requestPaymentForm.reset();
  };

  useEffect(() => {
    if (amountRequestWatch !== null && !isNaN(parseFloat(amountRequestWatch))) {
      const paymentEligibleToRequest =
        parseFloat(invoiceDetails?.total_amount_eligible_to_request ?? '0') -
        (parseFloat(amountRequestWatch) / 100) *
          parseFloat(invoiceDetails?.total_amount_eligible_for_payment ?? '0') +
        (isEdit && editData ? parseFloat(editData.amount_requested || '0') : 0);
      setPaymentEligibleToRequest(paymentEligibleToRequest);
      requestPaymentForm.setValue(
        'amount_request_decimal',
        parseFloat(getValues(`invoice.total_amount_eligible_to_request`) || '0') -
          paymentEligibleToRequest
      );
    } else {
      isEdit && editData
        ? setPaymentEligibleToRequest(
            parseFloat(invoiceDetails?.total_amount_eligible_to_request ?? '0') +
              parseFloat(editData.amount_requested)
          )
        : setPaymentEligibleToRequest(
            parseFloat(invoiceDetails?.total_amount_eligible_to_request ?? '0')
          );
    }
  }, [amountRequestWatch, invoiceDetails]);

  const requestAllAmount = () => {
    const percentAmountRequestable =
      isEdit && editData
        ? ((parseFloat(invoiceDetails?.total_amount_eligible_to_request ?? '0') +
            parseFloat(editData.amount_requested)) /
            parseFloat(invoiceDetails?.total_amount_eligible_for_payment ?? '0')) *
          100
        : (parseFloat(invoiceDetails?.total_amount_eligible_to_request ?? '0') /
            parseFloat(invoiceDetails?.total_amount_eligible_for_payment ?? '0')) *
          100;
    requestPaymentForm.setValue('amount_request', percentAmountRequestable.toString());
  };
  return (
    <Modal open={open} onClose={handleCancel}>
      <main className={css.mainWrapper}>
        <section className={css.modalHeader}>
          <Typography variant="h4">Request Payment</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </section>
        <form noValidate onSubmit={handleSubmit(onFormSubmit)} className={css.formWrapper}>
          <section className={css.modalContent}>
            <div className={css.autoCompleteWrapper}>
              {!isEdit && (
                <Controller
                  name="order"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AutoComplete
                      {...field}
                      label="Order"
                      placeholder="Start typing"
                      options={orderOptions}
                      disabled={isEdit}
                      onInputChange={(event: any, value: string) =>
                        fetchOrderDetails(event, { search_text: value })
                      }
                      onInputSelection={onOrderSelection}
                      keyOption="readable_order_id"
                      key="order_id"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      width="100%"
                    />
                  )}
                />
              )}
              <Controller
                name="supplier"
                control={control}
                render={({ field, fieldState }) => (
                  <AutoComplete
                    {...field}
                    label="Supplier"
                    placeholder="Start typing"
                    options={supplierOptions}
                    disabled={isEdit}
                    onInputChange={(event: any, value: string) =>
                      fetchSupplierDetails(event, { search_text: value, order_id: order?.order_id })
                    }
                    onInputSelection={onSupplierSelection}
                    keyOption="supplier_name"
                    key="supplier_id"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    width="100%"
                  />
                )}
              />
              <Controller
                name="invoice_number"
                control={control}
                render={({ field, fieldState }) => (
                  <AutoComplete
                    {...field}
                    required
                    label="Invoice"
                    placeholder="Start typing"
                    options={invoiceOptions}
                    disabled={isEdit}
                    onInputChange={(event: any, value: string) =>
                      fetchInvoiceDetails(event, {
                        search_text: value,
                        supplier_id: supplier?.supplier_id,
                        order_id: order?.order_id
                      })
                    }
                    onInputSelection={onInvoiceSelection}
                    keyOption="invoice_number"
                    key="invoice_info_id"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    width="100%"
                  />
                )}
              />
            </div>
            <div className={css.invoiceDetailsWrapper}>
              <div className={css.invoiceDetailsHeader}>
                <Controller
                  name={`invoice.invoice_date`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputDatePicker
                      {...field}
                      placeholder="Select date"
                      label="Invoice Date"
                      disabled
                      onSelect={(day: Date | undefined) => field.onChange(day ?? null)}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      rootClassName={css.fieldSpacing}
                    />
                  )}
                />
                <Controller
                  name={`invoice.currency`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <SelectLabel
                      {...field}
                      label="Currency"
                      isDisabled
                      placeholder="Select"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name={`invoice.gross_amount`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      disabled
                      enterKeyHint="next"
                      autoComplete="off"
                      label="Gross Amount"
                      placeholder="Start typing"
                      value={field.value ?? ''}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      endIcon={
                        fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                      }
                    />
                  )}
                />
              </div>
              <div className={css.rowWrapper}>
                <Controller
                  name={`invoice.payment_terms`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      disabled
                      value={field.value ?? ''}
                      enterKeyHint="next"
                      autoComplete="off"
                      label="Payment Terms"
                      placeholder="Start typing"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      endIcon={
                        fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                      }
                    />
                  )}
                />
                <Controller
                  name={`invoice.remarks`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      disabled
                      enterKeyHint="next"
                      autoComplete="off"
                      label="Remarks"
                      placeholder="Start typing"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      endIcon={
                        fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                      }
                    />
                  )}
                />
              </div>
              {invoiceWatch ? (
                <Button
                  variant="text"
                  onClick={() =>
                    setRequestPaymentState((prevState: IRequestPaymentState) => {
                      return {
                        ...prevState,
                        showAmountDetails: !prevState.showAmountDetails
                      };
                    })
                  }>
                  See how these amounts are calculated?
                </Button>
              ) : (
                <></>
              )}

              {showAmountDetails && (
                <div className={css.amountBreakdown}>
                  <div>
                    <b>
                      Gross Amount ({invoiceDetails?.currency}{' '}
                      {invoiceDetails?.gross_invoice_amount}){' '}
                    </b>
                    = Invoice Amount ({invoiceDetails?.currency} {invoiceDetails?.amount ?? 0}){' '}
                    {invoiceDetails?.gst_amount?.length
                      ? `+ GST Amount (${invoiceDetails?.currency} ${invoiceDetails?.gst_amount})`
                      : ``}
                  </div>
                  <div>
                    <b>
                      {' '}
                      Net Amount ({invoiceDetails?.currency} {getValues('invoice.net_amount')}) ={' '}
                    </b>
                    Gross Amount ({invoiceDetails?.currency} {invoiceDetails?.gross_invoice_amount})
                    {invoiceDetails?.tds_amount?.length &&
                    parseFloat(invoiceDetails?.tds_amount ?? '0')
                      ? ` - TDS Amount (${invoiceDetails?.currency} ${invoiceDetails?.tds_amount})`
                      : ''}
                    {invoiceDetails?.tcs_amount?.length &&
                    parseFloat(invoiceDetails?.tcs_amount ?? '0')
                      ? ` + TCS Amount (${invoiceDetails?.currency} ${invoiceDetails?.tcs_amount})`
                      : ''}
                    {invoiceDetails?.additional_charges_or_discounts?.length &&
                    parseFloat(invoiceDetails?.additional_charges_or_discounts ?? '0')
                      ? `${
                          parseFloat(invoiceDetails?.tcs_amount ?? '0') > 0
                            ? ` +/- Add. Charges or discounts  (${invoiceDetails?.currency} ${invoiceDetails?.additional_charges_or_discounts})`
                            : ''
                        }`
                      : ''}
                  </div>
                  <div>
                    <b>Total Amount Eligible to Request ({invoiceDetails?.currency} </b>
                    {paymentEligibleToRequest}) = Net Amount ({invoiceDetails?.currency}{' '}
                    {getValues('invoice.net_amount')})
                    {invoiceDetails?.total_amount_requested?.length &&
                    parseFloat(invoiceDetails?.total_amount_requested ?? '0')
                      ? ` - Total Amount Requested (${invoiceDetails?.currency} ${invoiceDetails?.total_amount_requested})`
                      : ''}
                    {invoiceDetails?.total_amount_initiated?.length &&
                    parseFloat(invoiceDetails?.total_amount_initiated ?? '0')
                      ? ` - Total Amount Initiated (${invoiceDetails?.currency} ${invoiceDetails?.total_amount_initiated})`
                      : ''}
                    {invoiceDetails?.total_amount_paid?.length &&
                    parseFloat(invoiceDetails?.total_amount_paid ?? '0')
                      ? ` - Total Amount Paid (${invoiceDetails?.currency} ${invoiceDetails?.total_amount_paid})`
                      : ''}
                    {invoiceDetails?.total_child_invoices_gross_amount?.length &&
                    parseFloat(invoiceDetails?.total_child_invoices_gross_amount ?? '0')
                      ? ` - Sum Total of other tax invoices (${invoiceDetails?.currency} ${invoiceDetails?.total_child_invoices_gross_amount})`
                      : ''}
                  </div>
                  {/* <div className={css.rowWrapper}>
                    <Controller
                      name={`invoice.amount`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled
                          enterKeyHint="next"
                          autoComplete="off"
                          label="Invoice Amount"
                          placeholder="Start typing"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          endIcon={
                            fieldState.invalid && (
                              <IconNode src={Images.alertError} alt="error icon" />
                            )
                          }
                        />
                      )}
                    />
                    <Controller
                      name={`invoice.gst_amount`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled
                          enterKeyHint="next"
                          autoComplete="off"
                          label="GST Amount"
                          placeholder="Start typing"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          endIcon={
                            fieldState.invalid && (
                              <IconNode src={Images.alertError} alt="error icon" />
                            )
                          }
                        />
                      )}
                    />
                  </div>
                  <div className={css.rowWrapper}>
                    <Controller
                      name={`invoice.additional_charges_or_discounts`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled
                          enterKeyHint="next"
                          autoComplete="off"
                          label="Additional Charges or Discounts"
                          placeholder="Start typing"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          endIcon={
                            fieldState.invalid && (
                              <IconNode src={Images.alertError} alt="error icon" />
                            )
                          }
                        />
                      )}
                    />
                    <Controller
                      name={`invoice.tds_amount`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled
                          enterKeyHint="next"
                          autoComplete="off"
                          label="TDS Amount"
                          placeholder="Start typing"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          endIcon={
                            fieldState.invalid && (
                              <IconNode src={Images.alertError} alt="error icon" />
                            )
                          }
                        />
                      )}
                    />
                    <Controller
                      name={`invoice.tcs_amount`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled
                          enterKeyHint="next"
                          autoComplete="off"
                          label="TCS Amount"
                          placeholder="Start typing"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          endIcon={
                            fieldState.invalid && (
                              <IconNode src={Images.alertError} alt="error icon" />
                            )
                          }
                        />
                      )}
                    />
                  </div>
                  <div className={css.rowWrapper}>
                    <Controller
                      name={`invoice.net_amount`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled
                          enterKeyHint="next"
                          autoComplete="off"
                          label="Net Amount"
                          placeholder="Start typing"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          endIcon={
                            fieldState.invalid && (
                              <IconNode src={Images.alertError} alt="error icon" />
                            )
                          }
                        />
                      )}
                    />
                    <Controller
                      name={`invoice.amount_el`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled
                          enterKeyHint="next"
                          autoComplete="off"
                          label="Net Amount"
                          placeholder="Start typing"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          endIcon={
                            fieldState.invalid && (
                              <IconNode src={Images.alertError} alt="error icon" />
                            )
                          }
                        />
                      )}
                    />
                  </div> */}
                </div>
              )}
            </div>
            <div className={css.rowWrapper}>
              <div>
                <Typography variant="label">Amount Eligible to Request</Typography>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  {!paymentEligibleToRequest && (
                    <Chip variant="filled-blue" label={`${invoiceDetails?.currency ?? ''} 0`} />
                  )}
                  {paymentEligibleToRequest > 0 ? (
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                        <Chip
                          variant="filled-green"
                          label={`${invoiceDetails?.currency ?? ''} ${paymentEligibleToRequest}`}
                        />
                        <Chip
                          variant="filled-green"
                          label={`${
                            paymentEligibleToRequest === 0
                              ? '0'
                              : (
                                  (paymentEligibleToRequest * 100) /
                                  parseFloat(
                                    invoiceDetails?.total_amount_eligible_for_payment ?? '1'
                                  )
                                ).toFixed(2)
                          } %`}
                        />
                        {invoiceWatch && paymentEligibleToRequest != 0 ? (
                          <Button
                            style={{ marginLeft: 10 }}
                            variant="text"
                            onClick={requestAllAmount}>
                            Request All
                          </Button>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <Chip variant="filled-red" label={paymentEligibleToRequest} />
                    </>
                  )}
                </div>
                {parseFloat(amountRequestWatch ?? '0') > 0 && (
                  <ToolTip
                    title={`Net Amount - Amount already requested - ${amountRequestWatch}% of Gross Amount `}
                    placement="right">
                    <span className={css.hoverText}>How is this calculated?</span>
                  </ToolTip>
                )}
              </div>
            </div>

            <div className={css.rowWrapper}>
              <Controller
                name="amount_request"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    value={field.value === null ? '' : field.value}
                    label="Request Amount %"
                    placeholder="Enter %"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    endIcon={
                      fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                    }
                  />
                )}
              />
              <Controller
                name="due_date"
                control={requestPaymentForm.control}
                render={({ field, fieldState }) => (
                  <InputDatePicker
                    {...field}
                    required
                    label="Due Date"
                    onSelect={(day: Date | undefined) => field.onChange(day ?? null)}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    rootClassName={css.fieldSpacing}
                  />
                )}
              />
            </div>
            <Controller
              name="requester_remarks"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value === null ? '' : field.value}
                  label="Requester Remarks"
                  placeholder="Start typing"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  endIcon={
                    fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                  }
                />
              )}
            />
          </section>
          <section className={css.modalFooter}>
            <Button variant="outlined-secondary" onClick={handleCancel}>
              Close
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving' : 'Save'}
            </Button>
          </section>
        </form>
      </main>
    </Modal>
  );
};

export default RequestPayment;
